<script lang="ts">
  import { i18n } from "../i18n.svelte";
  import { inview, smoothScroll } from "../actions";
  import SectionHead from "./SectionHead.svelte";
  import Sprout from "./Sprout.svelte";

  const p = $derived(i18n.t.pricing);
  const stages = [1, 2, 3, 4] as const;
</script>

<section class="section section--rule" id="pricing">
  <div class="container">
    <SectionHead label={p.label} title={p.title} lead={p.lead} align="center" />

    <div class="grid">
      {#each p.tiers as tier, i}
        <article
          class="tier reveal"
          class:featured={tier.featured}
          use:inview={{ delay: i * 80 }}
        >
          {#if tier.featured}<span class="badge">{tier.tagline}</span>{/if}
          <div class="head">
            <Sprout size={30} stage={stages[i]} />
            <div>
              <h3>{tier.name}</h3>
              <span class="kana">{tier.kana}</span>
            </div>
          </div>

          <div class="price">
            <strong>{tier.price}</strong>
            {#if tier.unit}<span>{tier.unit}</span>{/if}
          </div>
          {#if !tier.featured}<p class="tag">{tier.tagline}</p>{/if}

          <ul class="feats">
            {#each tier.features as feat}
              <li>
                <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M4 10.5l3.5 3.5L16 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <span>{feat}</span>
              </li>
            {/each}
          </ul>

          <a
            href="#cta"
            class="btn"
            class:btn-primary={tier.featured}
            class:btn-ghost={!tier.featured}
            use:smoothScroll
          >
            {tier.cta}
          </a>
        </article>
      {/each}
    </div>

    <p class="bill reveal" use:inview>{p.billingNote}</p>
  </div>
</section>

<style>
  .grid {
    margin-top: clamp(2.5rem, 1.5rem + 3vw, 4rem);
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    align-items: stretch;
  }
  .tier {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 1.8rem 1.4rem;
    background: var(--paper-card);
    border: 1px solid var(--line);
    border-radius: var(--radius);
  }
  .tier.featured {
    border-color: var(--green);
    box-shadow: 0 20px 50px -30px rgba(44, 110, 73, 0.6);
    background:
      radial-gradient(120% 60% at 50% 0%, rgba(95, 179, 100, 0.12), transparent 60%),
      var(--paper-card);
  }
  .badge {
    position: absolute;
    top: -0.8rem;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    font-family: var(--font-mono);
    font-size: 0.66rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    background: var(--green);
    color: #fff;
    padding: 0.4em 0.9em;
    border-radius: 999px;
  }
  .head {
    display: flex;
    align-items: center;
    gap: 0.7rem;
  }
  .head h3 {
    font-size: 1.3rem;
    line-height: 1.1;
  }
  .kana {
    font-family: var(--font-mono);
    font-size: 0.68rem;
    letter-spacing: 0.06em;
    color: var(--muted);
    text-transform: uppercase;
  }
  .price {
    margin-top: 1.2rem;
    display: flex;
    align-items: baseline;
    gap: 0.3rem;
  }
  .price strong {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 2rem;
    color: var(--ink);
  }
  .price span {
    font-size: 0.85rem;
    color: var(--muted);
  }
  .tag {
    margin-top: 0.4rem;
    font-size: 0.82rem;
    color: var(--terra);
  }
  .feats {
    margin: 1.3rem 0 1.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    flex: 1;
  }
  .feats li {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    font-size: 0.9rem;
    color: var(--ink-soft);
    line-height: 1.5;
  }
  .feats svg {
    flex: none;
    margin-top: 0.15em;
    color: var(--green);
  }
  .tier .btn {
    width: 100%;
    justify-content: center;
  }

  .bill {
    margin: 1.8rem auto 0;
    text-align: center;
    font-size: 0.82rem;
    color: var(--muted);
  }

  @media (max-width: 980px) {
    .grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 520px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
</style>
